import { Component, EventEmitter, Output } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { MatCardModule } from '@angular/material/card';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { environment } from '@environments/environment';
import { SharedModule } from '@informally/admin/shared/shared.module';
import { VerifyService } from '@suvo-bi-users';
import { lastValueFrom } from 'rxjs';

@Component({
  standalone: true,
  imports: [MatCardModule, RouterLink, SharedModule],
  template: `
    <div class="outer-container">
      <mat-card class="login-card">
        <div class="login-card-content">
          <div class="login-header">
            <a href="https://informally.io" class="brand-link">
              <img src="/assets/logo-informally.svg" />
            </a>
            <h1>{{ pageTitle }}</h1>
            <p>{{ pageSubtitle }}</p>
          </div>
          <lib-login
            [loginComponentOptions]="loginComponentOptions"
            (loginStatusChange)="onLoginStatusChange($event)"
          />

          <div class="register-cta">
            <span>New here?</span>
            <a [routerLink]="'/register'" [queryParams]="registerQueryParams">Create an account</a>
          </div>

          @if (message) {
            <p class="message">{{ message }}</p>
          }
        </div>
      </mat-card>

      <div class="support-copy">
        <h2>
          <a [routerLink]="supportLink">{{ supportLinkLabel }}</a>
        </h2>
        <p>
          By continuing, you agree to Informally's
          <a [href]="termsUrl" target="_blank" rel="noopener noreferrer">Terms of Service</a>
          and
          <a [href]="privacyUrl" target="_blank" rel="noopener noreferrer">Privacy Policy</a>, and
          to receive occasional product and marketing emails from Informally. You can unsubscribe at
          any time.
        </p>
      </div>
    </div>
  `,
  styles: `
    .outer-container {
      min-height: calc(100vh - max(48px, 21vh));
      width: 100%;
      background-color: #f6f7fb;
      padding: max(48px, 21vh) 16px 0;
      overflow-x: hidden;
      display: flex;
      flex-direction: column;
    }

    .login-card {
      width: 100%;
      max-width: 480px;
      margin: 0 auto;
      border-radius: 14px;
      border: 1px solid #e3e8f2;
      box-shadow: 0 14px 36px rgba(17, 24, 39, 0.09);
    }

    .login-card-content {
      padding: 34px 28px;
    }

    .login-header {
      max-width: 400px;
      width: 100%;
      margin: 0 auto 24px;
      text-align: left;
    }

    .brand-link {
      display: inline-block;
      margin-top: 8px;
    }

    .login-header h1 {
      margin: 20px 0 8px;
      line-height: 1.15;
      letter-spacing: -0.01em;
      color: #1f2a44;
    }

    .login-header p {
      margin: 0;
      color: #5c6478;
      font-size: 1rem;
      line-height: 1.45;
    }

    .message {
      text-align: center;
      margin: 16px 0 0;
      color: #445275;
    }

    .register-cta {
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid #e3e8f2;
      display: flex;
      justify-content: center;
      gap: 6px;
      color: #5c6478;
      font-size: 0.95rem;
    }

    .register-cta a {
      color: #445275;
      font-weight: 600;
      text-decoration: none;
    }

    .register-cta a:hover {
      text-decoration: underline;
    }

    .support-copy {
      width: 100%;
      max-width: 620px;
      margin: auto auto 0;
      padding: 24px 20px 24px;
      text-align: center;
    }

    .support-copy h2 {
      margin: 0 0 8px;
      font-size: 0.85rem;
      font-weight: 700;
    }

    .support-copy h2 a {
      color: #1f2a44;
      text-decoration: none;
    }

    .support-copy h2 a:hover {
      text-decoration: underline;
    }

    .support-copy p {
      margin: 0;
      color: #5c6478;
      font-size: 0.75rem;
      line-height: 1.6;
    }

    .support-copy a {
      color: #445275;
      font-weight: 600;
      text-decoration: none;
    }

    .support-copy a:hover {
      text-decoration: underline;
    }

    ::ng-deep .login-container {
      p {
        text-align: left;
        width: 400px;
      }
    }

    @media (max-width: 560px) {
      .login-card-content {
        padding: 26px 18px;
      }

      ::ng-deep .login-container {
        p {
          width: 100%;
        }
      }
    }
  `,
})
export class LoginPageComponent {
  @Output() loginStatusChange: EventEmitter<void> = new EventEmitter<void>();

  readonly termsUrl = 'https://informally.io/terms/';
  readonly privacyUrl = 'https://informally.io/privacy/';
  readonly pageTitle: string;
  readonly pageSubtitle: string;
  readonly supportLinkLabel: string;
  readonly supportLink: string;
  readonly registerQueryParams: { recipe: string } | undefined;

  loginComponentOptions = {
    titleText: 'Log In',
    showRegister: false,
    skipVerifyStep: true,
    pwResetFirebaseTenantId: environment.platformFirebaseTenantID,
    redirectAfterLogin: false,
  };

  message: string;
  private recipeOnboardingInProgress = false;
  private recipeOnboardingCompleted = false;
  private readonly recipe: string | null;

  constructor(
    activatedRoute: ActivatedRoute,
    private readonly fireAuth: AngularFireAuth,
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly verifyService: VerifyService,
  ) {
    this.pageTitle = activatedRoute.snapshot.data['pageTitle'] || 'Log in';
    this.pageSubtitle = activatedRoute.snapshot.data['pageSubtitle'] || 'Continue to informally';
    this.supportLinkLabel = activatedRoute.snapshot.data['supportLinkLabel'] || 'Need Help?';
    this.supportLink = activatedRoute.snapshot.data['supportLink'] || '/help-center/login';
    const recipeFromQuery = activatedRoute.snapshot.queryParamMap.get('recipe');
    const redirectUrl = activatedRoute.snapshot.queryParamMap.get('redirectUrl');
    this.recipe = recipeFromQuery ?? this.extractRecipeFromRedirectUrl(redirectUrl);
    this.registerQueryParams = this.recipe ? { recipe: this.recipe } : undefined;
  }

  async onLoginStatusChange(status) {
    if (status === 'USER_UNVERIFIED') {
      await this.verifyService.sendVerificationEmail();
      this.message = 'A verification link has been sent to your email address.';
    }

    if (status === 'LOGIN_ATTEMPT_COMPLETE') {
      await this.handleLoginSuccess();
    }

    this.loginStatusChange.next(status);
  }

  private async handleLoginSuccess() {
    if (this.recipeOnboardingInProgress) {
      return;
    }

    this.recipeOnboardingInProgress = true;

    try {
      if (this.recipe && !this.recipeOnboardingCompleted) {
        this.message = 'Creating your shop...';
        await this.createTenantFromRecipe(this.recipe);
        this.recipeOnboardingCompleted = true;
      }

      await this.router.navigate(['private', 'login-referrer'], {
        queryParamsHandling: 'preserve',
      });
    } catch (e) {
      this.message =
        e?.error?.message || e?.message || 'Login succeeded, but we could not create your shop.';
    } finally {
      this.recipeOnboardingInProgress = false;
    }
  }

  private async createTenantFromRecipe(tenantRecipeID: string): Promise<void> {
    const user = await this.fireAuth.currentUser;
    const token = await user?.getIdToken();

    await lastValueFrom(
      this.http.post(
        `${environment.dataServiceApi}tenant/existing`,
        { tenantRecipeID },
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      ),
    );

    if (token) {
      await lastValueFrom(
        this.http.post(
          `${environment.usersServiceApi}auth/refresh-claims`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        ),
      );
    }

    await user?.getIdToken(true);
  }

  private extractRecipeFromRedirectUrl(redirectUrl: string | null): string | null {
    if (!redirectUrl) {
      return null;
    }

    try {
      const decodedRedirectUrl = decodeURIComponent(redirectUrl);
      const parsedRedirectUrl = new URL(decodedRedirectUrl, window.location.origin);
      return parsedRedirectUrl.searchParams.get('recipe');
    } catch {
      return null;
    }
  }
}
